import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { WhatsappLogo, InstagramLogo, EnvelopeSimple, ChatCircleDots, X } from "@phosphor-icons/react";
import {
  WHATSAPP_URL, INSTAGRAM_URL, MAILTO_URL,
  trackContactClick,
} from "@/lib/contact";

// Pages where a bottom-anchored input lives — avoid covering the send button.
const HIDDEN_ROUTES = ["/coach", "/interview", "/onboarding"];

/**
 * Floating "Need help?" widget pinned to the bottom-right of every page.
 * Click the FAB → reveals a glass panel with WhatsApp / Instagram / Email.
 */
export default function ContactWidget() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Auto-close when route changes.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  if (HIDDEN_ROUTES.some((r) => location.pathname.startsWith(r))) return null;

  const trackAndClose = (channel) => {
    trackContactClick(channel, "floating-widget");
    setOpen(false);
  };

  return (
    <div
      ref={rootRef}
      className="fixed z-[70] right-4 bottom-4 sm:right-6 sm:bottom-6"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="contact-widget"
    >
      {/* Panel */}
      <div
        className={`absolute bottom-16 right-0 w-[min(86vw,320px)] origin-bottom-right transition-all duration-200 ease-out ${
          open ? "opacity-100 scale-100 pointer-events-auto translate-y-0" : "opacity-0 scale-95 pointer-events-none translate-y-1"
        }`}
        data-testid="contact-widget-panel"
        aria-hidden={!open}
      >
        <div className="bg-[#0F0F12]/85 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl shadow-black/50 p-2 overflow-hidden">
          <div className="px-3 pt-3 pb-2">
            <div className="text-[10px] tracking-[0.2em] uppercase text-blue-400 font-semibold">// Support</div>
            <div className="text-sm text-white mt-0.5">How can we help you today?</div>
          </div>

          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAndClose("whatsapp")}
            data-testid="widget-whatsapp"
            className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 transition-colors"
          >
            <span className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <WhatsappLogo size={18} weight="fill" className="text-emerald-400" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white font-medium">Chat on WhatsApp</div>
              <div className="text-xs text-zinc-500 truncate">Usually replies in minutes</div>
            </div>
          </a>

          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAndClose("instagram")}
            data-testid="widget-instagram"
            className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 transition-colors"
          >
            <span className="w-9 h-9 rounded-lg bg-pink-500/15 border border-pink-500/30 flex items-center justify-center">
              <InstagramLogo size={18} weight="fill" className="text-pink-400" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white font-medium">Follow on Instagram</div>
              <div className="text-xs text-zinc-500 truncate">@careergpsaionline</div>
            </div>
          </a>

          <a
            href={MAILTO_URL}
            onClick={() => trackAndClose("email")}
            data-testid="widget-email"
            className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 transition-colors"
          >
            <span className="w-9 h-9 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
              <EnvelopeSimple size={18} weight="fill" className="text-blue-400" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white font-medium">Send an email</div>
              <div className="text-xs text-zinc-500 truncate">helpcareergpsai@gmail.com</div>
            </div>
          </a>
        </div>
      </div>

      {/* FAB */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close support menu" : "Open support menu"}
        aria-expanded={open}
        data-testid="contact-widget-fab"
        className={`relative w-14 h-14 rounded-full inline-flex items-center justify-center transition-all duration-200 shadow-lg shadow-blue-600/30 ${
          open ? "bg-zinc-800 text-white scale-95" : "bg-blue-600 hover:bg-blue-500 text-white hover:scale-105"
        }`}
      >
        {open ? <X size={22} weight="bold" /> : <ChatCircleDots size={24} weight="fill" />}
        {!open && (
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#050505] animate-pulse" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}
